// 09-10 (Pau aprobó la maqueta «mantener el dedo», iPad): el menú de un agente en la escena, al mantener el dedo (o
// con el clic derecho). Lo mismo que se hace desde su chat, sin tener que abrirlo.
// 04-11 (mesas de equipo, grupos.md §4): «Mover a ▸» = los grupos de su sala, «Sin grupo», «Grupo nuevo… Ctrl G» y
// «Mover de mesa» (abre «Ordenar»). Con varias mesas elegidas (Ctrl+clic) y esta entre ellas: «Agrupar los N…».
// 05-11 (auditoría final flujo.md #10): las opciones son las de marco/AccionesAgente.tsx, las MISMAS que el menú de su
// fila en la lista (con su acción urgente arriba: Revisar · Sí · Cerrar); aquí solo se añade «Agrupar los N…», que solo
// existe en la escena (la elección de varias mesas).
import * as Menu from '@radix-ui/react-dropdown-menu';
import { Group } from 'lucide-react';
import { useOficinaStore } from '../store';
import { t } from '../../../i18n';
import type { ContratadoDTO } from '../compartido/vocabulario';
import { quien } from '../marco/estado';
import { CONTENIDO_MENU, OpcionesAgente, OpcionMenu, type PrimitivasMenu } from '../marco/AccionesAgente';
import { useAgente } from '../marco/useAgente';

const PRIMITIVAS: PrimitivasMenu = Menu;

/** Un destino de «Mover a»: un grupo de la sala (con cuántos tiene), o una sugerencia (vaciado hace < 7 días, n = 0). */
export interface DestinoGrupo {
  grupoId: string;
  nombre: string;
  n: number;
  /** Espera a Santi ahora (su contrato atado tiene la pelota en el lado de Santi). */
  conSanti: boolean;
}

export interface MoverAGrupo {
  grupos: readonly DestinoGrupo[];
  /** El grupo en el que está (null = «Sin grupo»). */
  actual: string | null;
  alMover: (grupoId: string | null) => void;
  alGrupoNuevo: () => void;
  /** Hay varias mesas elegidas y esta es una: «Agrupar los N…». */
  agrupar?: { n: number; alAgrupar: () => void };
}

export function MenuAgente({
  c,
  alCerrar,
  alAbrir,
  alMover,
  aparcado,
  grupos,
}: {
  c: ContratadoDTO;
  alCerrar: () => void;
  alAbrir: () => void;
  /** «Mover de mesa»: abre «Ordenar» de su sala. */
  alMover: () => void;
  /** Duerme según su marcador (lo que enseña su mesa): el menú dice lo mismo que la mesa. Sin él, su propio cálculo. */
  aparcado?: boolean;
  /** 04-11 — «Mover a ▸»: los grupos de su sala. */
  grupos?: MoverAGrupo;
}) {
  // Su estado con el mismo contexto que la lista (si duerme, si puede dormirse, su acción urgente).
  const agente = useAgente(c);
  const estado = aparcado === undefined ? agente : { ...agente, aparcado };
  const pedir = (que: 'renombrar' | 'cierre') => {
    const s = useOficinaStore.getState();
    if (que === 'renombrar') s.pedirRenombrar(c.agentId);
    else s.pedirCierre(c.agentId);
    alAbrir();
  };
  return (
    <Menu.Root open onOpenChange={(abierto) => !abierto && alCerrar()}>
      {/* El ancla es la mesa entera: el menú sale encima (o debajo) de ella, nunca bajo el dedo — Radix elige la
          opción sobre la que se LEVANTA el dedo, y el dedo sigue sobre el agente al abrirse. */}
      <Menu.Trigger asChild>
        <span className="pointer-events-none absolute inset-0" aria-hidden tabIndex={-1} />
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Content
          side="top"
          align="center"
          sideOffset={8}
          collisionPadding={8}
          // El ancla no tiene texto: el menú se nombra a sí mismo (Radix lo nombraría por el ancla).
          aria-labelledby={undefined}
          aria-label={t('oficinaV2.escena.acciones', { nombre: quien(c) })}
          className={CONTENIDO_MENU}
        >
          {grupos?.agrupar ? (
            <>
              <OpcionMenu M={PRIMITIVAS} icono={Group} atajo={t('oficinaV2.grupos.atajoAgrupar')} alElegir={grupos.agrupar.alAgrupar}>
                {t('oficinaV2.grupos.agruparLos', { n: grupos.agrupar.n })}
              </OpcionMenu>
              <Menu.Separator className="my-1 h-px bg-oficina-linea" />
            </>
          ) : null}
          <OpcionesAgente
            M={PRIMITIVAS}
            c={c}
            estado={estado}
            alAbrir={alAbrir}
            alRenombrar={() => pedir('renombrar')}
            alTerminar={() => pedir('cierre')}
            {...(grupos ? { moverA: grupos } : {})}
            alMoverDeMesa={alMover}
            atajoF2
          />
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
}
